import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled, { ThemeContext } from 'styled-components';

import ImageDesktop from 'images/pictographs/desktop.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import { PictographOption } from 'lib/interfaces/StepsConfig';
import ThemeSerialized, { PictographPosition, TextAlign } from 'lib/interfaces/ThemeSerialized';
import AnimatedCheck from 'verify/components/AnimatedCheck';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedSvg from 'verify/components/ThemedSvg';
import ThemedTitle from 'verify/components/ThemedTitle';

const Header = styled('div')`
  margin-bottom: ${SPACINGS.base * 8}px;
`;

const AnimationContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${SPACINGS.base * 5}px;
`;

// [PER-7484]: Defaults to centered text to preserve Persona-opinionated alignment.
// Change supports full explicit start-alignment for DoorDash request.
const TextDefaultCentered = styled('div')<{ theme: ThemeSerialized }>`
  text-align: ${({ theme }) =>
    theme.textAlign === TextAlign.Default ? 'center' : theme.textAlign};
`;

interface VerifySuccessViewProps {
  handleSubmit: (values: any) => Promise<void>;
  hasCompleteButton: boolean;
  isChildFlow: boolean;
  isProcessing: boolean;
  pictograph: PictographOption;
}

export default function VerifySuccessView(props: VerifySuccessViewProps) {
  const { handleSubmit, hasCompleteButton, isChildFlow, isProcessing, pictograph } = props;
  const [t] = useTranslation();
  const { successPictographPosition } = useContext(ThemeContext);

  const pictographNode = pictograph !== 'none' && (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <CenteredPictograph height={null} width={null} aria-label='success'>
      <CustomizablePictograph
        url='successPictographUrl'
        height='successPictographHeight'
        themed={false} // checkmark has built-in theming
      >
        <AnimationContainer>
          <AnimatedCheck />
        </AnimationContainer>
      </CustomizablePictograph>
    </CenteredPictograph>
  );

  if (isChildFlow) {
    return (
      <div data-test='verify-success-view__child-flow'>
        <Header>
          <ThemedTitle>{t('success.title-exchange-flow')}</ThemedTitle>
          <ThemedParagraph>
            <Markdown>{t('success.prompt-exchange-flow')}</Markdown>
          </ThemedParagraph>
        </Header>

        <CenteredPictograph aria-label='desktop pictograph'>
          <ThemedSvg vectorEffect='initial'>
            <ImageDesktop />
          </ThemedSvg>
        </CenteredPictograph>
      </div>
    );
  } else {
    return (
      <div>
        {successPictographPosition === PictographPosition.Start && pictographNode}

        <Header>
          <ThemedTitle>
            {/* We don't use the new ThemedParagraph for backwards compatibility; this is centered by default. */}
            <TextDefaultCentered>{t('success.title')}</TextDefaultCentered>
          </ThemedTitle>
          <TextDefaultCentered>
            <Markdown>{t('success.prompt')}</Markdown>
          </TextDefaultCentered>
        </Header>

        {successPictographPosition === PictographPosition.Center && pictographNode}

        {hasCompleteButton && (
          <PositionableButtonContainer>
            <Button
              id='success__button--complete'
              buttonType={ButtonType.Primary}
              onClick={handleSubmit}
              disabled={isProcessing}
              isPending={isProcessing}
            >
              {t('success.btn-submit')}
            </Button>
          </PositionableButtonContainer>
        )}
      </div>
    );
  }
}
